"use client";

import { useI18n } from "@/lib/i18n/I18nProvider";

const INTEGRATION_CATEGORIES = {
  pms: [
    "Opera PMS",
    "Fidelio",
    "Logus HMS",
    "Mews",
    "Cloudbeds",
    "Protel",
    "Hotelogix",
  ],
  posFood: ["iiko", "R-Keeper 7", "Micros Simphony", "Infrasys", "Lightspeed"],
  channel: ["TravelLine", "SiteMinder"],
  spa: ["Book4Time", "SpaSoft"],
  accounting: ["1С:Отель", "1С:Бухгалтерия", "SAP", "Oracle", "Sage Intacct", "Xero"],
  revenue: ["IDeaS", "Duetto", "RateGain"],
};

// Для EN — международный порядок без кириллических 1С-названий (1C латиницей).
const ACCOUNTING_EN = ["SAP", "Oracle", "Sage Intacct", "NetSuite", "Xero", "1C"];

const CATEGORIES_ORDER = ["pms", "posFood", "channel", "spa", "accounting", "revenue"] as const;

export function LogoBar(): JSX.Element {
  const { t, locale } = useI18n();

  // accounting — единственная локаль-зависимая категория (на EN без кириллических 1С).
  const categories: Record<keyof typeof INTEGRATION_CATEGORIES, string[]> = {
    ...INTEGRATION_CATEGORIES,
    accounting:
      locale === "en" ? ACCOUNTING_EN : INTEGRATION_CATEGORIES.accounting,
  };

  const getCategoryLabel = (category: keyof typeof INTEGRATION_CATEGORIES): string => {
    const labels: Record<keyof typeof INTEGRATION_CATEGORIES, string> = {
      pms: "PMS",
      posFood: "POS / F&B",
      channel: "Channel",
      spa: "SPA",
      accounting: "Accounting",
      revenue: "Revenue",
    };
    return labels[category];
  };

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50/50 border-y border-slate-100">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-lg font-semibold text-slate-700">
            {t("landing.logoBar.title")}
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            {t("landing.logoBar.subtitle")}
          </p>
        </div>

        {/* Desktop: Grid by category */}
        <div className="hidden md:grid grid-cols-2 lg:grid-cols-3 gap-8">
          {CATEGORIES_ORDER.map((category) => (
            <div key={category}>
              <h3 className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-4">
                {getCategoryLabel(category)}
              </h3>
              <div className="flex flex-wrap gap-2">
                {categories[category].map((integration) => (
                  <span
                    key={integration}
                    className="inline-block px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:border-slate-300 hover:bg-slate-50 transition-colors"
                  >
                    {integration}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: Flat list with category headers */}
        <div className="md:hidden space-y-6">
          {CATEGORIES_ORDER.map((category) => (
            <div key={category}>
              <h3 className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-3">
                {getCategoryLabel(category)}
              </h3>
              <div className="flex flex-wrap gap-2">
                {categories[category].map((integration) => (
                  <span
                    key={integration}
                    className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700"
                  >
                    {integration}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
